
import * as actionTypes from '../constants/action_types'
import * as collaborationActions from './collaboration'
import * as ficusActions from './ficus_map'
import { setFicusConfig, updateIterationOrder } from './ficus_config'
import { setAnalyzeMode, setFicusData } from './ficus_data'
import * as wsTypes from '../modules/websockets/server/constants/action_types'
import type { AnalyzeFrameworkBarType } from '../reducers/analyze_framework_bar'
import { removeMapLocation } from './analysis_panel'
import type {SelectFrameworkBarType} from '../reducers/select_framework_bar'

/**
 * @flow
 *
 * Handles dispatching events in response to messages sent from a websocket server
 * @param wsType: the Redux action wsType sent from the websocket server
 * @param payload: the JSON payload attached with the message
 */
export const onWebsocketEvent = (wsType: string, payload: object) => (dispatch, getState, { emit }) => {
    const state = getState()
    if (wsType === wsTypes.WS_CLIENT_ACTION) {
        const actionType = payload.operation.name
        // console.log(`Received ${wsType} - ${payload.operation.name}`)
        switch (actionType) {
        case actionTypes.OT_SHARE_SESSION: {
            // Another user has shared their session
            // Update the sessions list from the server
            dispatch(collaborationActions.fetchSessions())
            break
        }

        case actionTypes.OT_UNSHARE_SESSION: {
            // Another user has unshared their session
            // Leave the chat room
            dispatch(collaborationActions.emitLeftSession(payload.operation.params.token))
            // Update client state
            dispatch(collaborationActions.onReceiveLeaveSession(false, payload.operation.params.token))
            // Refresh the current sessions from the server
            dispatch(collaborationActions.fetchSessions())
            break
        }

        default: {
            dispatch({ type: wsTypes.WS_PRE_DISPATCH, payload })
            dispatch({ type: wsTypes.WS_CLIENT_ACTION, payload })
            // Allow all client-side events to fire before running post dispatch.
            // Without the timeout, events that are triggered as a result of the websocket event
            // get relayed back to the websocket server in a ping-pong match.  Using a timeout
            // allows the application to set a flag during pre-dispatch that prevents the ping pong madness.
            // Hacky but works for now.
            setTimeout(() => dispatch({ type: wsTypes.WS_POST_DISPATCH, payload }), 100)
        }
        }

        // Extra handling
        if (state.collaboration.enabled) {
            if (actionType === actionTypes.OT_CLOSE_POPUP) {
                const location = payload.operation.params.location
                if (payload.operation.originator !== state.token.value && getState().collaborationSettings.locations && location) {
                    dispatch(removeMapLocation(location))
                }
            } else if (actionType === actionTypes.OT_SET_FRAMEWORK) {
                const ficusNode = payload.operation.params.ficusNode
                if (payload.operation.originator !== state.token.value && getState().collaborationSettings.framework && ficusNode) {
                    dispatch(setFicusData(ficusNode))
                }
            } else if (actionType === actionTypes.OT_SET_ANALYZE_MODE) {
                const mode = payload.operation.params.analyzeMode
                if (payload.operation.originator !== state.token.value && getState().collaborationSettings.analyzeMode && mode) {
                    dispatch(setAnalyzeMode(mode))
                }
            } else if (actionType === actionTypes.OT_SET_FICUS_CONFIG) {
                const config: AnalyzeFrameworkBarType = payload.operation.params.config
                if (payload.operation.originator !== state.token.value && config) {
                    // disable playing -- only react to frame change.
                    config.started = false
                    // Unset iteration type because the local user's theme rasters may not have the same ordering.
                    //   Don't unset if the iteration type hasn't changed.
                    if (config.iterationType !== 'default' && config.iterationType) {
                        config.iterationType = undefined
                    }
                    dispatch(setFicusConfig(config))
                }
            }
        }
    }
}
